import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Slider from './Slider';
import AboutUs from '../../assets/images/about-us.png';
import TickCircle from '../../assets/images/tick-icon.svg';
import TickCircleLg from '../../assets/images/tick-lg.svg';
import Chinese from '../../assets/images/chinese.png';
import Subway from '../../assets/images/subway.png';
import Domino from '../../assets/images/domino.png';
import Delivering from '../../assets/images/delivering-food.png';
import KinnectApps from '../../assets/images/KinnectApps.png';
import PlayStore from '../../assets/images/play-store.svg';
import AppStore from '../../assets/images/app-store.svg';
import DriverQuestion from '../../assets/images/driver-question.svg';
import GettingStarted from '../../assets/images/getting-started.svg';
import PlaySubscribe from '../../assets/images/pay-subscribe.svg';
import RestaurantQuestion from '../../assets/images/restaurant-question.svg';
import Minus from '../../assets/images/minus.svg';
import Plus from '../../assets/images/plus.svg';
import Times from '../../assets/images/circle-cross.svg';
import Tick from '../../assets/images/circle-tick.svg';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import AOS from 'aos';
import 'aos/dist/aos.css'
import { endpoint } from '../../utils/endpoints';
import { fetchRestaurantCms, getCmsDescriptionText } from '../../utils/cms';
import Modal from '../../Components/Modal/Modal';
import Logo from '../../assets/images/logo.png';

const foodItemClasses =
  '[&_.item]:bg-[rgba(255,255,255,0.09)] [&_.item]:rounded-[20px] md:[&_.item]:rounded-[30px] [&_.item]:p-3.5 [&_.item]:h-full [&_.item]:flex [&_.item]:flex-col [&_.image]:shrink-0 [&_.image]:overflow-hidden [&_.image]:rounded-[12px] md:[&_.image]:rounded-[20px] [&_.image>img]:h-full [&_.image>img]:w-full [&_.image>img]:object-cover text-[26px] sm:text-[18px] md:text-[26px] 2xl:text-4xl [&_h3]:font-PxBold [&_h3]:line-clamp-2 [&_h3]:leading-tight text-white [&_p]:font-PxThin [&_p]:line-clamp-1 [&_.caption]:flex [&_.caption]:flex-col [&_.caption]:gap-y-0 2xl:[&_.caption]:gap-y-1 [&_.caption]:px-4 [&_.caption]:pt-4 md:[&_.caption]:py-7';

const foodItemMobileSizeClasses =
  '[&_.splide\_\_slide]:!w-[280px] [&_.item]:w-[280px] [&_.item]:h-[380px] [&_.image]:h-[200px] [&_.caption]:flex-1 [&_h3]:min-h-[3.25rem]';

const foodItemDesktopSizeClasses =
  '[&_.splide\_\_slide]:h-auto [&_.item]:min-h-[380px] md:[&_.item]:min-h-[440px] 2xl:[&_.item]:min-h-[520px] [&_.image]:h-[220px] md:[&_.image]:h-[280px] 2xl:[&_.image]:h-[340px]';

const centeredCarouselOptions = (width, height) => ({
  type: 'loop',
  focus: 'center',
  perPage: 1,
  perMove: 1,
  gap: '1rem',
  fixedWidth: width,
  fixedHeight: height,
  trimSpace: false,
  arrows: false,
  pagination: false,
  drag: true,
  snap: true,
  autoplay: false,
});

const foodCarouselOptions = centeredCarouselOptions('280px', '380px');
const desktopFoodCarouselOptions = {
  type: 'loop',
  perPage: 3,
  perMove: 1,
  gap: '2rem',
  arrows: false,
  pagination: false,
  drag: true,
  autoplay: true,
  interval: 2500,
  pauseOnHover: true,
  breakpoints: {
    1024: {
      gap: '1.5rem',
    },
  },
};
const packageCarouselOptions = centeredCarouselOptions('300px', '580px');
const faqCarouselOptions = centeredCarouselOptions('140px', '160px');

const faqTabMobileSizeClasses =
  '[&_.splide\_\_slide]:!w-[140px] [&_.faq-tab]:w-[140px] [&_.faq-tab]:h-[160px]';

const packageCardClasses =
  'plan-card bg-[#29292A] rounded-2xl p-4 md:p-8 relative flex flex-col h-full';

const PAYMENT_SESSION_STORAGE_KEY = 'aikinnectRestaurantSubscriptionCheckout';

const getCheckoutSessionData = (result) => {
  if (result?.data?.object === 'checkout.session') return result.data;
  if (result?.object === 'checkout.session') return result;
  return result?.data?.session || result?.session || result?.data || null;
};

const readStoredCheckoutSession = () => {
  if (typeof window === 'undefined') return null;

  try {
    const storedSession = window.sessionStorage.getItem(PAYMENT_SESSION_STORAGE_KEY);
    return storedSession ? JSON.parse(storedSession) : null;
  } catch (error) {
    return null;
  }
};

const formatStripeAmount = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '';
  const numericAmount = Number(amount);
  if (Number.isNaN(numericAmount)) return String(amount);

  return `${(numericAmount / 100).toFixed(2)} ${String(currency || '').toUpperCase()}`.trim();
};

const getPaymentDetailRows = ({ session, plan, sessionId, result }) => {
  const metadata = session?.metadata || {};
  const displaySessionId = result === 'Failed' ? sessionId : sessionId || session?.id;

  return [
    { label: 'Result', value: result },
    { label: 'Session ID', value: displaySessionId },
    { label: 'Amount', value: formatStripeAmount(session?.amount_total, session?.currency) },
    { label: 'Plan', value: plan?.name },
    { label: 'Plan ID', value: metadata.subscription_plan_id || plan?.id },
    { label: 'Subscription Payment ID', value: metadata.subscription_payment_id },
  ].filter((item) => item.value !== null && item.value !== undefined && item.value !== '');
};

const packageMobileSizeClasses =
  '[&_.splide\_\_slide]:!w-[300px] [&_.plan-card]:w-[300px] [&_.plan-card]:h-[580px] [&_.plan-card_ul]:flex-1 [&_.plan-card_ul]:min-h-0 [&_.plan-card_ul]:overflow-y-auto [&_.plan-card_li]:text-sm';

const getCurrentLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }

   
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => reject(error),
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });

const fallbackFoods = [
  { image: Chinese, title: 'Paneer Hakka Noodles', category: 'Chinese' },
  { image: Subway, title: 'Italian Chopped Salad', category: 'Subway' },
  { image: Domino, title: 'Pepperoni Pizza', category: "Domino's" },
];

const getValidTopRatedDishes = (list) =>
  list
    .filter((dish) => dish?.dish_name && dish?.restaurant_name && dish?.image)
    .slice(0, 5)
    .map((dish) => ({
      image: dish.image,
      title: dish.dish_name,
      category: dish.restaurant_name,
    }));

const defaultFaqContent = {
  start: [
    {
      q: "How to get started?",
      a: "Download the app, sign up with your details, and follow the onboarding steps to set up your profile.",
    },
  ],
  pay: [
    {
      q: "What Kind Payment Available?",
      a: "We support credit cards, debit cards, PayPal, and UPI payments for your convenience.",
    },
    {
      q: "How Can I Use Subscription?",
      a: "Log in to your account, navigate to subscriptions, pick a plan, pay, and enjoy premium features instantly.",
    },
  ],
  driver: [
    {
      q: "How can I become a driver?",
      a: "Submit your documents, complete the verification process, and you can start accepting rides.",
    },
  ],
  restaurant: [
    {
      q: "How do I add my restaurant?",
      a: "Register on our partner portal, upload your menu and documents, and get approved to start receiving orders.",
    },
  ],
};

const getFaqListFromPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.faqs)) return payload.data.faqs;
  if (Array.isArray(payload?.data?.faq)) return payload.data.faq;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const getValidFaqItems = (payload) =>
  getFaqListFromPayload(payload)
    .map((item) => ({
      q: item?.question || item?.faq_question || item?.title || item?.q,
      a: item?.answer || item?.faq_answer || item?.description || item?.a,
    }))
    .filter((item) => item.q && item.a);

const faqRoleByTabId = {
  driver: 'Driver',
  restaurant: 'Restaurant',
};

const getPlanFeatures = (callUsageLimit) => [
  { text: callUsageLimit ? `${callUsageLimit} AI calls` : 'AI calls', included: true },
  { text: 'Online onboarding', included: true },
  { text: 'Customer app', included: true },
  { text: 'Restaurant app', included: true },
  { text: 'Driver app', included: true },
  {
    text: 'Restaurant driver management for external and freelance drivers',
    included: true,
  },
];

const defaultPlans = [
  {
    id: 'silver',
    name: 'Silver',
    badge: 'Most Popular',
    badgeVariant: 'outline',
    price: '120',
    currencySymbol: '£',
    features: getPlanFeatures(300),
    planType: 'subscription',
  },
  {
    id: 'gold',
    name: 'Gold',
    badge: 'Premium',
    badgeVariant: 'filled',
    price: '185',
    currencySymbol: '£',
    features: getPlanFeatures(600),
    planType: 'subscription',
  },
  {
    id: 'platinum',
    name: 'Platinum',
    badge: 'Premium',
    badgeVariant: 'filled',
    price: '305',
    currencySymbol: '£',
    features: getPlanFeatures(1200),
    planType: 'subscription',
  },
];

const getPlanAmount = (amount) => String(amount || '0').replace(/\.00$/, '');

const getSubscriptionPlanList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const getCurrencySymbol = (currency) => {
  const normalized = String(currency || '').toLowerCase();
  if (normalized === 'gbp') return '£';
  if (normalized === 'usd') return '$';
  if (normalized === 'eur') return '€';
  return '£';
};

const getValidSubscriptionPlans = (payload) =>
  getSubscriptionPlanList(payload)
    .filter((plan) => plan?.name && plan?.amount)
    .map((plan, index) => ({
      id: plan._id || plan.name,
      name: plan.name,
      badge: index === 0 ? 'Most Popular' : 'Premium',
      badgeVariant: index === 0 ? 'outline' : 'filled',
      price: getPlanAmount(plan.amount),
      currencySymbol: getCurrencySymbol(plan.currency),
      features: getPlanFeatures(plan.call_usage_limit),
      planType: 'subscription',
    }));

const getValidAdditionalPlans = (payload) =>
  getSubscriptionPlanList(payload)
    .filter((plan) => plan?.name && plan?.amount && plan?.status === 1)
    .map((plan) => ({
      id: plan._id || plan.name,
      name: plan.name,
      price: getPlanAmount(plan.amount),
      currencySymbol: getCurrencySymbol(plan.currency),
      addOnCalls: plan.add_on_calls,
      billingCycle: plan.billing_cycle,
      stripePriceId: plan.stripe_price_id,
      planType: 'additional',
    }));

const getPurchasedAddOnIds = (subscription) => {
  const items = subscription?.additional_calls;
  if (!Array.isArray(items) || !items.length) return [];

  return items
    .map((item) => {
      if (typeof item === 'string') return item;
      if (typeof item !== 'object' || !item) return null;

      const planRef = item.plan_id || item.additional_plan_id || item.subscription_plan_id;
      if (typeof planRef === 'object') return planRef?._id || null;

      return planRef || item._id || null;
    })
    .filter(Boolean);
};

const isSubscriptionExpired = (subscription) => {
  const expiryDate = subscription?.expiry_date;
  if (!expiryDate) return false;
  return new Date(expiryDate).getTime() < Date.now();
};

const parseRestaurantSubscription = (payload) => {
  const sub =
    payload?.data?.subscription ??
    payload?.data?.data?.subscription ??
    payload?.subscription;

  if (!sub || typeof sub !== 'object') return { status: 'none' };

  const planRef = sub.subscription_plan_id;
  const planId =
    (typeof planRef === 'object' ? planRef?._id : planRef) ||
    sub.plan_id ||
    null;
  const planName =
    (typeof planRef === 'object' ? planRef?.name : null) ||
    sub.plan_name ||
    '';
  const subscriptionStatus = String(sub.subscription_status ?? '').toUpperCase();
  const purchasedAddOnIds = getPurchasedAddOnIds(sub);
  const expiryDate = sub.expiry_date || null;
  const inactiveStatuses = ['EXPIRED', 'CANCELLED', 'CANCELED', 'INACTIVE'];
  const expired =
    isSubscriptionExpired(sub) ||
    inactiveStatuses.includes(subscriptionStatus);
  const isActive =
    subscriptionStatus === 'ACTIVE' &&
    !expired &&
    sub.status !== 0 &&
    sub.payment_status !== 0 &&
    Boolean(planId || planName);

  if (isActive) {
    return {
      status: 'active',
      planId,
      name: planName,
      expiryDate,
      purchasedAddOnIds,
      raw: sub,
    };
  }

  if (planId || planName) {
    return {
      status: 'expired',
      planId,
      name: planName,
      expiryDate,
      purchasedAddOnIds,
      raw: sub,
    };
  }

  return { status: 'none' };
};

const getCountdownTime = (totalSeconds) => {
  return {
    days: Math.floor(totalSeconds / (60 * 60 * 24)),
    hours: Math.floor((totalSeconds / (60 * 60)) % 24),
    minutes: Math.floor((totalSeconds / 60) % 60),
    seconds: totalSeconds % 60,
  };
};

const formatCountdownValue = (value) => String(value).padStart(2, '0');

function FaqTabCard({ tab, isActive, onSelect, className = '' }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`faq-tab w-full h-full text-center rounded-[20px] flex flex-col items-center justify-center gap-2 cursor-pointer transition ${className} ${
        isActive
          ? 'bg-gradient-to-t from-[#2590F3] via-[#26BBF8] to-[#2CF6FE] shadow-lg'
          : 'border border-[rgba(255,255,255,0.4)] hover:border-cyan-400'
      }`}
    >
      <img
        src={tab.icon}
        alt={tab.label}
        className={`h-10 ${isActive ? 'opacity-100' : 'opacity-25'}`}
      />
      <p className="text-base font-PxBold leading-tight text-white px-2">{tab.label}</p>
    </button>
  );
}

function PlanToggle({ enabled, onChange, disabled = false }) {
  return (
    <div className="flex items-center justify-between gap-3 mt-4">
      <span className="text-sm font-PoppinsL text-white/80">Additional Calls</span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={disabled}
        onClick={() => onChange(!enabled)}
        className={`relative w-12 h-6 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          enabled
            ? 'bg-gradient-to-t from-[#2590F3] via-[#26BBF8] to-[#2CF6FE]'
            : 'bg-gray-600'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
            enabled ? 'translate-x-6' : ''
          }`}
        />
      </button>
    </div>
  );
}

function AddOnSelector({ addOns, selectedAddOnId, purchasedAddOnIds = [], onSelect }) {
  if (!addOns.length) return null;

  return (
    <div className="flex flex-col gap-2 flex-1">
      <p className="text-sm font-PoppinsL text-white/80 mb-1">Select Additional Calls</p>
      <div className="flex flex-col gap-2">
        {addOns.map((addOn) => {
          const isPurchased = purchasedAddOnIds.includes(addOn.id);
          const isSelected = selectedAddOnId === addOn.id;

          return (
            <button
              key={addOn.id}
              type="button"
              onClick={() => {
                if (isPurchased) return;
                onSelect(isSelected ? null : addOn.id);
              }}
              disabled={isPurchased}
              className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                isPurchased
                  ? 'border-emerald-400/60 bg-emerald-500/10 cursor-default'
                  : isSelected
                    ? 'border-cyan-400 bg-gradient-to-r from-[#2590F3]/20 via-[#26BBF8]/20 to-[#2CF6FE]/20'
                    : 'border-[rgba(255,255,255,0.2)] hover:border-cyan-400/60'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-PoppinsM text-sm md:text-base">{addOn.name}</p>
                  <p className="text-xs text-white/70 mt-1">
                    {addOn.addOnCalls} extra AI calls
                    {isPurchased && (
                      <span className="ml-2 text-emerald-400 font-PoppinsM">Purchased</span>
                    )}
                  </p>
                </div>
                <p className="font-PoppinsB text-sm md:text-base whitespace-nowrap">
                  {addOn.currencySymbol}
                  {addOn.price}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PlanCard({
  plan,
  additionalPlans,
  isActivePlan = false,
  isExpiredPlan = false,
  isPendingConfirm = false,
  pendingConfirmPlanId = null,
  hasActiveSubscription = false,
  purchasedAddOnIds = [],
  isUpgrading = false,
  isConfirming = false,
  showAdditional,
  onToggleAdditional,
  selectedAddOnId,
  onSelectAddOn,
  onSubscribe,
  onConfirm,
  onUpgrade,
}) {
  const badgeClass =
    plan.badgeVariant === 'outline'
      ? 'border border-gray-500 text-white text-xs md:text-sm px-3 text-nowrap md:px-5 py-2.5 rounded-full font-PoppinsL relative sm:-right-3'
      : 'bg-[#2590F3] text-white text-xs md:text-sm px-3 text-nowrap md:px-5 py-2.5 rounded-full font-PoppinsL relative sm:-right-3';

  const showToggle = isActivePlan && additionalPlans.length > 0;
  const showAddOnView = showToggle && showAdditional;

  const selectedAddOn =
    showAddOnView && selectedAddOnId
      ? additionalPlans.find((addOn) => addOn.id === selectedAddOnId) || null
      : null;
  const canUpgrade =
    selectedAddOn && !purchasedAddOnIds.includes(selectedAddOn.id);

  const isHighlighted = isActivePlan || isExpiredPlan || isPendingConfirm;
  const cardClasses = `${packageCardClasses} ${
    isHighlighted ? 'ring-2 ring-[#2CF6FE] border border-[#2CF6FE]' : ''
  }`;

  const planHeader = (
    <div className="flex items-center justify-between">
      <h2 className="font-PoppinsM text-xl md:text-2xl">{plan.name}</h2>
      <div className={badgeClass}>{plan.badge}</div>
    </div>
  );

  return (
    <div className={cardClasses}>
      {isActivePlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-t from-[#2590F3] via-[#26BBF8] to-[#2CF6FE] text-white text-xs font-PoppinsM px-4 py-1 rounded-full whitespace-nowrap">
          Current Plan
        </div>
      )}
      {isExpiredPlan && !isActivePlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-500/90 text-white text-xs font-PoppinsM px-4 py-1 rounded-full whitespace-nowrap">
          Expired Plan
        </div>
      )}
      {!showAddOnView ? (
        <>
          {planHeader}
          {showToggle && (
            <PlanToggle enabled={showAdditional} onChange={onToggleAdditional} />
          )}
          <p className="text-4xl font-PoppinsB mt-3">
            {plan.currencySymbol || '£'}{plan.price}{' '}
            <span className="text-lg font-PoppinsL">/ Month</span>
          </p>
          {plan.description && (
            <p className="mt-4 font-PoppinsL text-sm md:pr-20 leading-relaxed">{plan.description}</p>
          )}
          <ul className="mt-6 flex flex-col gap-3 flex-1">
            {plan.features.map((feature) => (
              <li key={feature.text} className="flex items-center gap-2.5">
                <span>
                  <img src={feature.included ? Tick : Times} className="min-w-5" alt="" />
                </span>
                {feature.text}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          {planHeader}
          <PlanToggle enabled={showAdditional} onChange={onToggleAdditional} />
          <AddOnSelector
            addOns={additionalPlans}
            selectedAddOnId={selectedAddOnId}
            purchasedAddOnIds={purchasedAddOnIds}
            onSelect={onSelectAddOn}
          />
        </>
      )}
      {isActivePlan ? (
        <button
          type="button"
          onClick={() => onUpgrade(plan, selectedAddOn)}
          disabled={!canUpgrade || isUpgrading}
          className="mt-10 md:mt-14 md:-mx-3 bg-gradient-to-t from-[#2590F3] via-[#26BBF8] to-[#2CF6FE] text-white text-lg font-PoppinsL py-3 rounded-full flex justify-center shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUpgrading ? 'Processing...' : 'Upgrade Subscription'}
        </button>
      ) : isPendingConfirm ? (
        <button
          type="button"
          onClick={() => onConfirm(plan)}
          disabled={isConfirming}
          className="mt-10 md:mt-14 md:-mx-3 bg-gradient-to-t from-[#2590F3] via-[#26BBF8] to-[#2CF6FE] text-white text-lg font-PoppinsL py-3 rounded-full flex justify-center shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isConfirming ? 'Redirecting to Payment...' : 'Confirm'}
        </button>
      ) : hasActiveSubscription || (pendingConfirmPlanId && !isPendingConfirm) ? null : (
        <button
          type="button"
          onClick={() => onSubscribe(plan)}
          className="mt-10 md:mt-14 md:-mx-3 bg-gradient-to-t from-[#2590F3] via-[#26BBF8] to-[#2CF6FE] text-white text-lg font-PoppinsL py-3 rounded-full flex justify-center shrink-0"
        >
          Subscribe Now
        </button>
      )}
    </div>
  );
}

function FoodCard({ food }) {
  return (
    <div className='item'>
      <div className='image'>
        <img src={food.image} alt={food.title} />
      </div>
      <div className='caption'>
        <h3>{food.title}</h3>
        <p>{food.category}</p>
      </div>
    </div>
  );
}

function Home() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionId: routeSessionId } = useParams();
  const [activeTab, setActiveTab] = useState("pay");
  const [totalDeliveredOrders, setTotalDeliveredOrders] = useState(null);
  const [foods, setFoods] = useState(fallbackFoods);
  const [cmsData, setCmsData] = useState(null);
  const [faqContent, setFaqContent] = useState(defaultFaqContent);
  const [plans, setPlans] = useState(defaultPlans);
  const [additionalPlans, setAdditionalPlans] = useState([]);
  const [additionalToggles, setAdditionalToggles] = useState({});
  const [selectedAddOnByPlanId, setSelectedAddOnByPlanId] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [lastExpiredPlanId, setLastExpiredPlanId] = useState(null);
  const [purchasedAddOnIds, setPurchasedAddOnIds] = useState([]);
  const [isRestaurantLoggedIn, setIsRestaurantLoggedIn] = useState(
    () => Boolean(localStorage.getItem('restaurantSessionToken'))
  );
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [pendingConfirmPlanId, setPendingConfirmPlanId] = useState(null);
  const [confirmingPlanId, setConfirmingPlanId] = useState(null);
  const [countdownSeconds, setCountdownSeconds] = useState(100 * 24 * 60 * 60);
  const countdown = getCountdownTime(countdownSeconds);
  const [locationError, setLocationError] = useState('');
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPaymentSuccessModalOpen, setIsPaymentSuccessModalOpen] = useState(false);
  const [isPaymentFailedModalOpen, setIsPaymentFailedModalOpen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const aboutUsCms = cmsData?.about_us;
  const aboutUsDescription =
    getCmsDescriptionText(aboutUsCms) ||
    'Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry.';
  const tabs = [
    { id: "start", label: "Getting Started", icon: GettingStarted },
    { id: "pay", label: "Pay & Subscribe", icon: PlaySubscribe },
    { id: "driver", label: "Driver Question", icon: DriverQuestion },
    { id: "restaurant", label: "Restaurant Question", icon: RestaurantQuestion },
  ];
  useEffect(() => {
    AOS.init({
      duration: 1000, // Animation duration in ms
      once: true,     // Whether animation should happen only once
    });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdownSeconds((seconds) => Math.max(seconds - 1, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const getTotalDeliveredOrders = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_ACCOUNTANT_API}${endpoint.totalDeliveredOrders}`
        );
        const result = await response.json();

        console.log('Total delivered orders:', result);
        setTotalDeliveredOrders(result?.data?.totalDeliveredOrders);
      } catch (error) {
        console.error('Error fetching total delivered orders:', error);
      }
    };

    getTotalDeliveredOrders();
  }, []);

  useEffect(() => {
    const getRestaurantCms = async () => {
      try {
        const result = await fetchRestaurantCms();

        console.log('Restaurant CMS:', result);
        setCmsData(result?.data);
      } catch (error) {
        console.error('Error fetching restaurant CMS:', error);
      }
    };

    getRestaurantCms();
  }, []);

  useEffect(() => {
    const getSubscriptionPlans = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_ACCOUNTANT_API}${endpoint.subscriptionPlans}`
        );
        const result = await response.json();

        console.log('Subscription plans:', result);
        const validPlans = getValidSubscriptionPlans(result);

        if (validPlans.length > 0) {
          setPlans(validPlans);
        }
      } catch (error) {
        console.error('Error fetching subscription plans:', error);
      }
    };

    getSubscriptionPlans();
  }, []);

  useEffect(() => {
    const getAdditionalCallsPlans = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_ACCOUNTANT_API}${endpoint.additionalCallsPlans}`
        );
        const result = await response.json();

        console.log('Additional calls plans:', result);
        const validAdditionalPlans = getValidAdditionalPlans(result);

        if (validAdditionalPlans.length > 0) {
          setAdditionalPlans(validAdditionalPlans);
        }
      } catch (error) {
        console.error('Error fetching additional calls plans:', error);
      }
    };

    getAdditionalCallsPlans();
  }, []);

  const matchPlanId = (subscriptionInfo) => {
    const matchedPlan =
      plans.find((p) => p.id === subscriptionInfo.planId) ||
      plans.find(
        (p) => p.name?.toLowerCase() === subscriptionInfo.name?.toLowerCase()
      ) ||
      null;

    return matchedPlan?.id || subscriptionInfo.planId;
  };

  const clearSubscriptionState = () => {
    setCurrentSubscription(null);
    setPurchasedAddOnIds([]);
    setAdditionalToggles({});
    setSelectedAddOnByPlanId({});
    setPendingConfirmPlanId(null);
  };

  const applySubscriptionState = (parsed) => {
    if (parsed.status === 'active') {
      const activePlanId = matchPlanId(parsed);

      setCurrentSubscription({
        planId: activePlanId,
        name: parsed.name,
        expiryDate: parsed.expiryDate,
        purchasedAddOnIds: parsed.purchasedAddOnIds,
        raw: parsed.raw,
      });
      setLastExpiredPlanId(null);
      setPendingConfirmPlanId(null);
      setPurchasedAddOnIds(parsed.purchasedAddOnIds);

      const nextSelectedAddOn = {};
      if (parsed.purchasedAddOnIds.length > 0) {
        nextSelectedAddOn[activePlanId] = parsed.purchasedAddOnIds[parsed.purchasedAddOnIds.length - 1];
      }

      setAdditionalToggles({ [activePlanId]: true });
      setSelectedAddOnByPlanId(nextSelectedAddOn);
      return;
    }

    clearSubscriptionState();

    if (parsed.status === 'expired') {
      setLastExpiredPlanId(matchPlanId(parsed));
      return;
    }

    setLastExpiredPlanId(null);
  };

  useEffect(() => {
    const token = localStorage.getItem('restaurantSessionToken');
    if (!token) return;

    const loadCurrentSubscription = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_ACCOUNTANT_API}${endpoint.getCurrentRestaurantSubscription}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const result = await response.json();
        applySubscriptionState(parseRestaurantSubscription(result));
      } catch (error) {
        console.error('Error loading current subscription:', error);
      }
    };

    loadCurrentSubscription();
  }, [plans]);

  const handleAdditionalToggle = (planId, enabled) => {
    setAdditionalToggles((current) => ({
      ...current,
      [planId]: enabled,
    }));

    if (!enabled) {
      setSelectedAddOnByPlanId((current) => ({
        ...current,
        [planId]: null,
      }));
    }
  };

  const handleAddOnSelect = (planId, addOnId) => {
    setSelectedAddOnByPlanId((current) => ({
      ...current,
      [planId]: addOnId,
    }));
  };

  const fetchedFaqRolesRef = useRef(new Set());

  const getFaqByRole = useCallback(async (role) => {
    if (fetchedFaqRolesRef.current.has(role)) return;

    fetchedFaqRolesRef.current.add(role);

    try {
      const params = new URLSearchParams({ role });
      const response = await fetch(
        `${process.env.REACT_APP_ACCOUNTANT_API}${endpoint.faq}?${params.toString()}`
      );
      const result = await response.json();

      console.log(`${role} FAQ:`, result);
      const validFaqItems = getValidFaqItems(result);

      if (validFaqItems.length > 0) {
        setFaqContent((prevFaqContent) => ({
          ...prevFaqContent,
          [role === 'Driver' ? 'driver' : 'restaurant']: validFaqItems,
        }));
      }
    } catch (error) {
      fetchedFaqRolesRef.current.delete(role);
      console.error(`Error fetching ${role} FAQ:`, error);
    }
  }, []);

  const getTopRatedDishes = useCallback(async () => {
    setIsRequestingLocation(true);
    setLocationError('');

    try {
      if (navigator.permissions?.query) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        console.log('Geolocation permission state:', permission.state);
      }

      const { latitude, longitude } = await getCurrentLocation();
      console.log('Current location:', { latitude, longitude });
      const params = new URLSearchParams({
        longitude: String(longitude),
        latitude: String(latitude),
      });
      const response = await fetch(
        `${process.env.REACT_APP_ACCOUNTANT_API}${endpoint.topRatedDishes}?${params.toString()}`
      );
      const result = await response.json();

      console.log('Top rated dishes:', result);
      const validDishes = getValidTopRatedDishes(result?.data?.dishes || []);

      if (validDishes.length > 0) {
        setFoods(validDishes);
      }
    } catch (error) {
      console.error('Error fetching top rated dishes:', error);
      setLocationError('Please allow location access to show nearby favourite foods.');
    } finally {
      setIsRequestingLocation(false);
    }
  }, []);

  useEffect(() => {
    getTopRatedDishes();
  }, [getTopRatedDishes]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get('payment') || params.get('payment_status') || params.get('status');
    const successStatus = params.get('success');
    const redirectStatus = params.get('redirect_status');
    const sessionIdFromUrl =
      params.get('session_id') ||
      params.get('id') ||
      params.get('checkout_session_id') ||
      routeSessionId ||
      '';
    const routePaymentResult =
      location.pathname.startsWith('/subscription/success')
        ? 'success'
        : location.pathname.startsWith('/subscription/cancel')
          ? 'failed'
          : '';
    const statePaymentResult = location.state?.paymentResult;
    const stateSessionId = location.state?.sessionId;
    const routeSessionIdForResult = routePaymentResult === 'success' ? sessionIdFromUrl : '';

    if (routePaymentResult) {
      navigate('/', {
        replace: true,
        state: {
          paymentResult: routePaymentResult,
          sessionId: routeSessionIdForResult,
        },
      });
      return;
    }

    const isPaymentSuccess =
      statePaymentResult === 'success' ||
      paymentStatus === 'success' ||
      paymentStatus === 'paid' ||
      successStatus === 'true' ||
      successStatus === '1' ||
      redirectStatus === 'succeeded';
    const isPaymentFailed =
      statePaymentResult === 'failed' ||
      paymentStatus === 'cancel' ||
      paymentStatus === 'cancelled' ||
      paymentStatus === 'failed' ||
      successStatus === 'false' ||
      redirectStatus === 'failed';

    if (isPaymentSuccess) {
      const storedCheckout = readStoredCheckoutSession();
      setPaymentDetails(getPaymentDetailRows({
        session: storedCheckout?.session,
        plan: storedCheckout?.plan,
        sessionId: sessionIdFromUrl || stateSessionId,
        result: 'Success',
      }));
      setIsPaymentSuccessModalOpen(true);
      navigate('/', { replace: true });
    } else if (isPaymentFailed) {
      const storedCheckout = readStoredCheckoutSession();
      setPaymentDetails(getPaymentDetailRows({
        session: storedCheckout?.session,
        plan: storedCheckout?.plan,
        sessionId: '',
        result: 'Failed',
      }));
      setIsPaymentFailedModalOpen(true);
      navigate('/', { replace: true });
    }
  }, [location.pathname, location.search, location.state, navigate, routeSessionId]);

  const scrollToSubscribeSection = () => {
    setTimeout(() => {
      document.getElementById('subscribe')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const fetchRestaurantSubscription = async (token) => {
    const response = await fetch(
      `${process.env.REACT_APP_ACCOUNTANT_API}${endpoint.getCurrentRestaurantSubscription}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const result = await response.json();
    console.log('Current restaurant subscription:', result);
    return parseRestaurantSubscription(result);
  };

  const handlePostLoginSubscription = (parsedSubscription, planToConfirm) => {
    applySubscriptionState(parsedSubscription);
    closeSubscriptionLogin();

    if (parsedSubscription.status === 'active') {
      scrollToSubscribeSection();
      return;
    }

    if (planToConfirm?.id) {
      setPendingConfirmPlanId(planToConfirm.id);
      scrollToSubscribeSection();
    }
  };

  const openSubscriptionLogin = (plan, selectedAddOn = null) => {
    setSelectedPlan({
      ...plan,
      selectedAddOn,
    });
    setLoginForm({ email: '', password: '' });
    setShowPassword(false);
    setLoginError('');
    setIsLoginModalOpen(true);
  };

  const closeSubscriptionLogin = () => {
    setIsLoginModalOpen(false);
    setLoginLoading(false);
    setShowPassword(false);
    setLoginError('');
  };

  const getStripeCheckoutUrl = (result) =>
    result?.data?.checkout_url ||
    result?.data?.checkoutUrl ||
    result?.data?.payment_url ||
    result?.data?.paymentUrl ||
    result?.data?.url ||
    result?.checkout_url ||
    result?.checkoutUrl ||
    result?.payment_url ||
    result?.paymentUrl ||
    result?.url;

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
    if (loginError) setLoginError('');
  };

  const handlePaymentSuccessClose = () => {
    setIsPaymentSuccessModalOpen(false);
    window.sessionStorage.removeItem(PAYMENT_SESSION_STORAGE_KEY);
    navigate('/', { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePaymentFailedClose = () => {
    setIsPaymentFailedModalOpen(false);
    window.sessionStorage.removeItem(PAYMENT_SESSION_STORAGE_KEY);
    navigate('/', { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubscriptionLogin = async (e) => {
    e.preventDefault();

    if (!selectedPlan?.id) {
      setLoginError('Please select a subscription plan');
      return;
    }
    if (!loginForm.email.trim()) {
      setLoginError('Email is required');
      return;
    }
    if (!loginForm.password.trim()) {
      setLoginError('Password is required');
      return;
    }

    setLoginLoading(true);
    setLoginError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_ACCOUNTANT_API}${endpoint.restaurantLogin}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginForm.email.trim(),
          password: loginForm.password,
        }),
      });
      const result = await response.json();

      if (!response.ok || result?.code >= 400 || result?.success === false) {
        setLoginError(result?.message || 'Login failed');
        return;
      }

      const token = result?.data?.token || result?.data?.accessToken || result?.token;
      if (token) localStorage.setItem('restaurantSessionToken', token);
      setIsRestaurantLoggedIn(true);

      const parsedSubscription = await fetchRestaurantSubscription(token);
      handlePostLoginSubscription(parsedSubscription, selectedPlan);
    } catch (error) {
      console.error('Restaurant login failed:', error);
      setLoginError('Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleConfirmSubscription = async (plan) => {
    if (!plan?.id || confirmingPlanId) return;

    const token = localStorage.getItem('restaurantSessionToken');
    setConfirmingPlanId(plan.id);

    try {
      const checkoutResponse = await fetch(
        `${process.env.REACT_APP_ACCOUNTANT_API}${endpoint.restaurantSubscriptionCheckout}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            plan_id: plan.id,
          }),
        }
      );
      const checkoutResult = await checkoutResponse.json();

      if (!checkoutResponse.ok || checkoutResult?.code >= 400 || checkoutResult?.success === false) {
        window.alert(checkoutResult?.message || 'Unable to start Stripe payment');
        return;
      }

      const checkoutUrl = getStripeCheckoutUrl(checkoutResult);
      if (checkoutUrl) {
        const checkoutSession = getCheckoutSessionData(checkoutResult);
        window.sessionStorage.setItem(
          PAYMENT_SESSION_STORAGE_KEY,
          JSON.stringify({
            session: checkoutSession,
            plan,
          })
        );
        window.location.href = checkoutUrl;
        return;
      }

      window.alert('Stripe checkout URL is missing from API response.');
    } catch (error) {
      console.error('Subscription checkout failed:', error);
      window.alert('Unable to start payment. Please try again.');
    } finally {
      setConfirmingPlanId(null);
    }
  };

  const handleUpgradeSubscription = async (plan, selectedAddOn) => {
    if (!selectedAddOn?.id || isUpgrading) return;

    const token = localStorage.getItem('restaurantSessionToken');
    setIsUpgrading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ACCOUNTANT_API}${endpoint.additionalCallsCheckout}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            plan_id: selectedAddOn.id,
          }),
        }
      );
      const result = await response.json();

      if (!response.ok || result?.code >= 400 || result?.success === false) {
        console.error('Additional calls checkout failed:', result);
        window.alert(result?.message || 'Unable to start the upgrade payment.');
        return;
      }

      const checkoutUrl = getStripeCheckoutUrl(result);
      if (checkoutUrl) {
        const checkoutSession = getCheckoutSessionData(result);
        window.sessionStorage.setItem(
          PAYMENT_SESSION_STORAGE_KEY,
          JSON.stringify({
            session: checkoutSession,
            plan: { ...plan, selectedAddOn },
          })
        );
        window.location.href = checkoutUrl;
        return;
      }

      window.alert('Stripe checkout URL is missing from API response.');
    } catch (error) {
      console.error('Additional calls checkout failed:', error);
      window.alert('Upgrade failed. Please try again.');
    } finally {
      setIsUpgrading(false);
    }
  };

  const [openIndex, setOpenIndex] = useState(null);
  const faqSplideRef = useRef(null);

  const selectFaqTab = (tabId, index) => {
    setActiveTab(tabId);
    setOpenIndex(null);
    faqSplideRef.current?.splide?.go(index);

    const role = faqRoleByTabId[tabId];
    if (role) {
      getFaqByRole(role);
    }
  };

  return (
    <>
        <Slider/>
        <section className="my-16 px-6 md:px-10 2xl:px-20 md:overflow-x-hidden md:py-32">
          <div className="grid sm:grid-cols-2 gap-10 items-center max-w-screen-2xl mx-auto md:px-6 text-white relative before:hidden md:before:block before:absolute before:left-48 md:before:left-96 before:right-[-24px] md:before:-right-full before:-inset-y-4 md:before:-inset-y-16 before:bg-[#29292A] before:rounded-tl-[50px] before:rounded-bl-[50px]">
            <div className="relative">
              <img src={AboutUs} alt="Pizza Order" className="rounded-2xl shadow-lg" />              
              <div className="absolute top-5 md:top-8 left-4 md:left-8 bg-white p-2 2xl:p-3.5 pr-4 2xl:pr-6 rounded-full shadow inline-flex items-center gap-x-2.5 text-black text-base 2xl:text-lg font-PxBold">
                <img src={TickCircleLg} className="w-8 2xl:w-auto"/> Order Complete
              </div>
              <div className="absolute -bottom-24 inset-x-0 mx-auto bg-white p-4 md:p-6 2xl:p-8 inline-flex flex-col gap-y-2.5 rounded-[20px] md:rounded-[30px] shadow-lg max-w-[300px] md:max-w-[350px] 2xl:max-w-[400px] text-black text-xl md:text-2xl 2xl:text-3xl font-PxBlack">
                <div><span className="text-[#26BBF8]">{totalDeliveredOrders ?? '800+'}</span> Orders Completed</div>
                <div className="text-gray-500 text-[15px] leading-relaxed font-PxRegular">Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt</div>
              </div>
            </div>
            <div className="mt-20 sm:mt-0 relative md:max-w-[75%] md:pl-3 sm:py-10 md:py-0 text-center sm:text-left">
              <p className="mb-2 text-[20px] md:text-[24px] 2xl:text-[32px] flex items-center justify-center sm:justify-start gap-x-3 before:content-[''] before:relative before:w-16 md:before:w-32 before:h-0.5 before:bg-gradient-to-l before:from-[#28CDFA] before:via-[#29292A8C] before:to-[#29292A66] after:content-[''] after:w-16 md:after:w-32 after:h-0.5 after:bg-gradient-to-l after:from-[#29292A66] after:via-[#29292A8C] after:to-[#28CDFA]">About Us</p>
              <h2 className="text-[24px] md:text-[35px] 2xl:text-[45px] leading-snug font-PxMedium mb-4">
                A New Way To Order Your Food By AI Kinnect
              </h2>
              <p className="mb-6 font-PxRegular text-base 2xl:text-lg">
                <span className="line-clamp-4">{aboutUsDescription}</span>
                <Link to="/about-us" className="text-[#26BBF8] hover:font-PxBold">
                  ...see more
                </Link>
              </p>
              
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-12 2xl:gap-x-[20rem] gap-y-4 2xl:gap-y-8 text-lg 2xl:text-xl font-PxRegular mt-8 md:mt-12">
                <p className="flex items-center gap-x-4">
                  <span className="shrink-0"><img src={TickCircle} alt="" className="min-w-5"/></span>
                  <span className="whitespace-nowrap">AI-Powered Ordering</span>
                </p>
                <p className="flex items-center gap-x-4">
                  <span className="shrink-0"><img src={TickCircle} alt="" className="min-w-5"/></span>
                  <span className="whitespace-nowrap">Real-Time Delivery Updates</span>
                </p>
                <p className="flex items-center gap-x-4">
                  <span className="shrink-0"><img src={TickCircle} alt="" className="min-w-5"/></span>
                  <span className="whitespace-nowrap">Restaurant Management Tools</span>
                </p>
                <p className="flex items-center gap-x-4">
                  <span className="shrink-0"><img src={TickCircle} alt="" className="min-w-5"/></span>
                  <span className="whitespace-nowrap">Smart Driver Assignment</span>
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="my-16 md:px-16 2xl:px-20 overflow-x-visible py-14 md:py-20 2xl:py-32 bg-[#29292A]">
          <div className='max-w-screen-2xl mx-auto px-6 md:px-0'>
            <div className='fancy-heading [&_h2]:text-[24px] md:[&_h2]:text-[36px] 2xl:[&_h2]:text-[45px] leading-tight md:leading-normal text-white [&_h2]:font-PxBold text-center mb-10 md:mb-14 2xl:mb-20 flex flex-col items-center'>
              <h2 className='flex items-center gap-x-3 before:relative before:w-16 md:before:w-32 before:h-0.5 before:bg-gradient-to-l before:from-[#28CDFA] before:via-[#29292A8C] before:to-[#29292A66] after:content-[""] after:w-16 md:after:w-32 after:h-0.5 after:bg-gradient-to-l after:from-[#29292A66] after:via-[#29292A8C] after:to-[#28CDFA]'>Find Healthy And Favourite Foods Near You</h2>
              <p className='font-PxThin md:text-lg mt-4 md:mt-0'>Lorem ipsum is a placeholder text commonly used to demonstrate</p>
              {locationError && (
                <div className="mt-5 flex flex-col items-center gap-3 text-sm font-PxRegular text-white">
                  <p>{locationError}</p>
                  <button
                    type="button"
                    onClick={getTopRatedDishes}
                    disabled={isRequestingLocation}
                    className="rounded-full bg-gradient-to-t from-[#2590F3] via-[#26BBF8] to-[#2CF6FE] px-5 py-2 font-PxBold text-white disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isRequestingLocation ? 'Requesting Location...' : 'Allow Location'}
                  </button>
                </div>
              )}
            </div>
            <div className={`sm:hidden ${foodItemClasses} ${foodItemMobileSizeClasses}`}>
              <Splide options={foodCarouselOptions} aria-label="Favourite foods">
                {foods.map((food, index) => (
                  <SplideSlide key={`${food.title}-${food.category}-${index}`}>
                    <FoodCard food={food} />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
            <div className={`hidden sm:block ${foodItemClasses} ${foodItemDesktopSizeClasses}`}>
              <Splide options={desktopFoodCarouselOptions} aria-label="Favourite foods">
                {foods.map((food, index) => (
                  <SplideSlide key={`${food.title}-${food.category}-${index}`}>
                    <FoodCard food={food} />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          </div>
        </section>
        <section className="my-16 md:px-16 2xl:px-20 overflow-hidden md:py-32">
          <div className='max-w-screen-2xl mx-auto grid grid-cols-12 items-center relative before:hidden md:before:block before:absolute before:right-[290px] 2xl:before:right-[390px] before:-left-full before:-inset-y-12 before:bg-[#29292A] before:rounded-tr-[50px] before:rounded-br-[50px] px-6 md:px-0'>
            <div className='[&_h2]:text-[30px] md:[&_h2]:text-[38px] mt-4 md:mt-0 2xl:[&_h2]:text-[45px] text-white [&_h2]:font-PxBold col-span-12 sm:col-span-5 2xl:col-span-4 [&_p]:font-PxRegular relative'>
              <h2 className='leading-tight mb-3'>Make Money Delivering Food Work Anywhere, Anytime.</h2>
              <p>Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry.</p>
              <ul className='text-lg 2xl:text-xl font-PxThin mt-6 md:mt-10 flex flex-col gap-y-5 [&_li]:flex [&_li]:items-center [&_li]:gap-x-2 md:[&_li]:gap-x-4'>
                <li><img src={TickCircle} className="h-4 md:h-auto"/> Lorem Ipsum is simply dummy text</li>
                <li><img src={TickCircle} className="h-4 md:h-auto"/> Lorem Ipsum is simply dummy text</li>
                <li><img src={TickCircle} className="h-4 md:h-auto"/> Lorem Ipsum is simply dummy text</li>
              </ul>
            </div>
            <div className='col-span-12 sm:col-span-7 text-center md:col-start-6 relative -order-1 sm:order-none'>
              <img src={Delivering} className='mx-auto block sm:w-[90%] 2xl:w-auto'/>
            </div>
          </div>
        </section>
        <section className="mt-16 md:px-16 2xl:px-20 overflow-hidden py-14 md:py-32 bg-[#29292A]">
          <div className='max-w-screen-2xl mx-auto grid grid-cols-12 items-center relative before:absolute before:right-[390px] before:-left-full before:-inset-y-12 before:rounded-tr-[50px] before:rounded-br-[50px]'>
            <div className='col-span-12 sm:col-span-6 relative sm:pl-6 md:pl-0'>
              <img src={KinnectApps} className='block mx-auto max-w-[80%] sm:max-w-full'/>
            </div>
            <div className='[&_h2]:text-[30px] md:[&_h2]:text-[38px] 2xl:[&_h2]:text-[45px] text-white [&_h2]:font-PxBold col-span-12 sm:col-span-6 2xl:col-span-5 relative md:col-start-7 md:pl-20 px-6 md:px-0 mt-6 md:mt-0'>
              <h2 className='leading-tight mb-3'>Get the AI Kinnect Apps</h2>
              <p className='2xl:pr-24 font-PxRegular md:font-PoppinsL'>Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry.</p>
              <ul className='md:text-lg 2xl:text-xl font-PoppinsL mt-6 md:mt-8 2xl:mt-10 flex flex-col gap-y-4 2xl:gap-y-5 [&_li]:flex [&_li]:items-center [&_li]:gap-x-2 md:[&_li]:gap-x-4'>
                <li><img src={TickCircle} className="h-4 md:h-auto"/> Customer App</li>
                <li><img src={TickCircle} className="h-4 md:h-auto"/> Driver App</li>
                <li><img src={TickCircle} className="h-4 md:h-auto"/> Restaurant App</li>
              </ul>
              <div className='grid grid-cols-3 [&_h3]:text-2xl md:[&_h3]:text-3xl font-PoppinsR [&_div]:flex [&_div]:flex-col [&_div]:gap-y-2 py-8 2xl:py-9 text-sm md:text-base [&_p]:font-PoppinsL text-center md:text-left'>
                <div>
                  <h3>15+</h3>
                  <p>Total Downloads</p>
                </div>
                <div>
                  <h3>8k</h3>
                  <p>Reviews</p>
                </div>
                <div>
                  <h3>4.8/5</h3>
                  <p>Customer Satification</p>
                </div>
              </div>
              <div className='flex [&_img]:h-14 2xl:[&_img]:h-16 gap-x-2.5'>
                <Link><img src={PlayStore}/></Link>
                <Link><img src={AppStore}/></Link>
              </div>
            </div>
          </div>
        </section>
        <section id="subscribe" className="bg-[#121212] min-h-screen 2xl:px-4 py-14 md:py-24">
            <div className='fancy-heading [&_h2]:text-[24px] md:[&_h2]:text-[36px] 2xl:[&_h2]:text-[45px] text-white [&_h2]:font-PxBold text-center mb-10 md:mb-16 flex flex-col items-center'>
              <h2 className='flex items-center gap-x-3 before:relative before:w-16 md:before:w-32 before:h-0.5 before:bg-gradient-to-l before:from-[#28CDFA] before:via-[#29292A8C] before:to-[#29292A66] after:content-[""] after:w-16 md:after:w-32 after:h-0.5 after:bg-gradient-to-l after:from-[#29292A66] after:via-[#29292A8C] after:to-[#28CDFA]'>Subscribe Package</h2>
              <p className='font-PxThin text-lg'>Lorem ipsum is a placeholder text commonly used to demonstrate</p>
            </div>
            <div className={`sm:hidden max-w-screen-2xl mx-auto w-full px-6 text-white ${packageMobileSizeClasses}`}>
              <Splide options={packageCarouselOptions} aria-label="Subscribe packages">
                {plans.map((plan) => (
                  <SplideSlide key={plan.id}>
                    <PlanCard
                      plan={plan}
                      additionalPlans={additionalPlans}
                      isActivePlan={currentSubscription?.planId === plan.id}
                      isExpiredPlan={lastExpiredPlanId === plan.id}
                      isPendingConfirm={pendingConfirmPlanId === plan.id}
                      pendingConfirmPlanId={pendingConfirmPlanId}
                      hasActiveSubscription={Boolean(currentSubscription)}
                      purchasedAddOnIds={purchasedAddOnIds}
                      isUpgrading={isUpgrading}
                      isConfirming={confirmingPlanId === plan.id}
                      showAdditional={Boolean(additionalToggles[plan.id])}
                      onToggleAdditional={(enabled) => handleAdditionalToggle(plan.id, enabled)}
                      selectedAddOnId={selectedAddOnByPlanId[plan.id] || null}
                      onSelectAddOn={(addOnId) => handleAddOnSelect(plan.id, addOnId)}
                      onSubscribe={openSubscriptionLogin}
                      onConfirm={handleConfirmSubscription}
                      onUpgrade={handleUpgradeSubscription}
                    />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
            <div className="hidden sm:grid sm:grid-cols-3 gap-x-4 md:gap-x-10 2xl:gap-x-20 max-w-screen-2xl mx-auto w-full px-6 md:px-16 2xl:px-12 text-white">
              {plans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  additionalPlans={additionalPlans}
                  isActivePlan={currentSubscription?.planId === plan.id}
                  isExpiredPlan={lastExpiredPlanId === plan.id}
                  isPendingConfirm={pendingConfirmPlanId === plan.id}
                  pendingConfirmPlanId={pendingConfirmPlanId}
                  hasActiveSubscription={Boolean(currentSubscription)}
                  purchasedAddOnIds={purchasedAddOnIds}
                  isUpgrading={isUpgrading}
                  isConfirming={confirmingPlanId === plan.id}
                  showAdditional={Boolean(additionalToggles[plan.id])}
                  onToggleAdditional={(enabled) => handleAdditionalToggle(plan.id, enabled)}
                  selectedAddOnId={selectedAddOnByPlanId[plan.id] || null}
                  onSelectAddOn={(addOnId) => handleAddOnSelect(plan.id, addOnId)}
                  onSubscribe={openSubscriptionLogin}
                  onConfirm={handleConfirmSubscription}
                  onUpgrade={handleUpgradeSubscription}
                />
              ))}
            </div>
        </section>
        <section className='bg-[#29292A] py-14 md:py-20 2xl:py-28'>
          <div className='max-w-screen-2xl mx-auto w-full text-white text-center'>
            <h2 className='text-[45px] md:text-[85px] 2xl:text-[115px] font-PxBold'>Coming Soon</h2>
            <p className='md:text-lg 2xl:text-xl font-PxThin px-6 md:px-0'>Our smarter AI-powered ordering experience is launching soon.</p>
            <div className='flex [&_h3]:text-[38px] md:[&_h3]:text-[60px] 2xl:[&_h3]:text-[70px] [&_h3]:font-PoppinsM [&_h3]:tabular-nums [&_h3]:border [&_h3]:border-[#FFFFFF36] [&_h3]:bg-[#1E1E1F] [&_h3]:rounded-[18px] md:[&_h3]:rounded-[24px] 2xl:[&_h3]:rounded-[34px] [&_h3]:w-[75px] md:[&_h3]:w-[100px] 2xl:[&_h3]:w-[120px] [&_h3]:h-20 md:[&_h3]:h-24 2xl:[&_h3]:h-32 [&_h3]:flex [&_h3]:justify-center [&_h3]:items-center [&_h3]:mb-3 [&_.item]:text-center font-PxRegular justify-center my-8 gap-x-2 md:gap-x-6 2xl:gap-x-10'>
              <div className='item'>
                <h3>{formatCountdownValue(countdown.days)}</h3>
                DAYS
              </div>
              <div className='item'>
                <h3>{formatCountdownValue(countdown.hours)}</h3>
                HOURS
              </div>
              <div className='item'>
                <h3>{formatCountdownValue(countdown.minutes)}</h3>
                MINUTE
              </div>
              <div className='item'>
                <h3>{formatCountdownValue(countdown.seconds)}</h3>
                SECONDS
              </div>
            </div>
            <button class="mt-2 2xl:mt-8 bg-gradient-to-t from-[#2590F3] via-[#26BBF8] to-[#2CF6FE] text-white text-lg font-PoppinsL py-3 px-8 md:px-20 rounded-full">
              Learn More About Aikinnect
            </button>
          </div>
        </section>
        <section className="bg-[#121212] px-6 md:px-40 2xl:px-4 py-14 md:py-24" id="faq">
          <div className='fancy-heading [&_h2]:text-[24px] md:[&_h2]:text-[36px] 2xl:[&_h2]:text-[45px] text-white [&_h2]:font-PxBold text-center mb-10 2xl:mb-16 flex flex-col items-center'>
            <h2 className='flex items-center gap-x-3 before:relative before:w-16 md:before:w-32 before:h-0.5 before:bg-gradient-to-l before:from-[#28CDFA] before:via-[#29292A8C] before:to-[#29292A66] after:content-[""] after:w-16 md:after:w-32 after:h-0.5 after:bg-gradient-to-l after:from-[#29292A66] after:via-[#29292A8C] after:to-[#28CDFA]'>Frequently Asked Questions</h2>
          </div>
          <div className="md:max-w-6xl mx-auto  text-white py-4">
            <div className={`md:hidden ${faqTabMobileSizeClasses}`}>
              <Splide
                ref={faqSplideRef}
                options={{
                  ...faqCarouselOptions,
                  start: tabs.findIndex((t) => t.id === activeTab),
                }}
                aria-label="FAQ categories"
                onMoved={(splide) => {
                  const index = splide.index % tabs.length;
                  if (tabs[index]) {
                    setActiveTab(tabs[index].id);
                    setOpenIndex(null);
                  }
                }}
              >
                {tabs.map((tab, index) => (
                  <SplideSlide key={tab.id}>
                    <FaqTabCard
                      tab={tab}
                      isActive={activeTab === tab.id}
                      onSelect={() => selectFaqTab(tab.id, index)}
                    />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
            <div className="hidden md:flex md:justify-center gap-4 md:gap-6">
              {tabs.map((tab, index) => (
                <FaqTabCard
                  key={tab.id}
                  tab={tab}
                  isActive={activeTab === tab.id}
                  onSelect={() => selectFaqTab(tab.id, index)}
                  className="md:w-[270px] md:h-44 2xl:h-48 md:rounded-[34px] md:gap-5 [&_img]:md:h-auto [&_p]:md:text-lg 2xl:[&_p]:text-2xl"
                />
              ))}
            </div>
            <div className="mt-6 md:mt-12">
              {(faqContent[activeTab] || []).map((item, idx) => (
                <div
                  key={idx}
                  className="border-b border-white-700 py-4 md:py-7 2xl:py-10"
                >
                  <button
                    onClick={() =>
                      setOpenIndex(openIndex === idx ? null : idx)
                    }
                    className="flex items-center justify-between w-full text-left"
                  >
                    <h2 className="text-xl md:text-[26px] 2xl:text-[32px] font-PxBold pr-3 flex-1">{item.q}</h2>
                    <span className="shrink-0 flex items-center justify-center size-5 md:size-6">
                      <img
                        src={openIndex === idx ? Minus : Plus}
                        alt=""
                        className="max-w-full max-h-full w-auto h-auto object-contain"
                      />
                    </span>
                  </button>
                  {openIndex === idx && (
                    <p className="mt-2 text-gray-400 text-lg font-PxThin">
                      {item.a}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
        <Modal isOpen={isLoginModalOpen} onClose={closeSubscriptionLogin}>
          <div className="py-8">
            <div className='border-b border-[#71777D] flex justify-center pt-1 pb-6'>
              <img src={Logo} alt="AI Kinnect" />
            </div>
            <form
              onSubmit={handleSubscriptionLogin}
              className="px-8 text-white [&_.form-control]:h-12 [&_.form-control]:text-black [&_.form-control]:outline-none [&_.form-control]:rounded-full [&_.form-control]:px-5 [&_.form-control]:mt-1 flex flex-col gap-y-5 pt-10 font-PxThin"
            >
              {selectedPlan && (
                <div className="rounded-2xl border border-[#71777D] p-4 text-center">
                  <p className="text-sm text-white/70">Selected Plan</p>
                  <h3 className="font-PxBold text-xl">{selectedPlan.name}</h3>
                  <p>
                    {selectedPlan.currencySymbol || '£'}
                    {selectedPlan.price} / Month
                  </p>
                </div>
              )}
              <div className="form-group flex flex-col">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={loginForm.email}
                  onChange={handleLoginInputChange}
                  disabled={loginLoading}
                />
              </div>
              <div className="form-group flex flex-col">
                <label>Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className="form-control w-full pr-12"
                    value={loginForm.password}
                    onChange={handleLoginInputChange}
                    disabled={loginLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    disabled={loginLoading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 disabled:opacity-50"
                  >
                    {showPassword ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M3 3L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M10.6 10.6A2 2 0 0 0 13.4 13.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M9.3 5.3A8.9 8.9 0 0 1 12 5C16.5 5 20.3 8 22 12C21.4 13.4 20.5 14.7 19.4 15.7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M16.7 18.7A9.3 9.3 0 0 1 12 20C7.5 20 3.7 17 2 12C2.8 10.1 4.1 8.5 5.7 7.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M2 12C3.7 8 7.5 5 12 5C16.5 5 20.3 8 22 12C20.3 16 16.5 19 12 19C7.5 19 3.7 16 2 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 15A3 3 0 1 0 12 9A3 3 0 0 0 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              {loginError && <p className="text-sm text-red-400">{loginError}</p>}
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-gradient-to-t from-[#2590F3] via-[#26BBF8] via-46% to-[#2CF6FE] h-14 rounded-full mt-3 disabled:opacity-60"
              >
                {loginLoading ? 'Logging In...' : 'Log In'}
              </button>
            </form>
          </div>
        </Modal>
        <Modal isOpen={isPaymentSuccessModalOpen} onClose={handlePaymentSuccessClose}>
          <div className="px-8 py-10 text-center text-white">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-t from-[#2590F3] via-[#26BBF8] to-[#2CF6FE]">
              <img src={Tick} alt="" className="h-9 w-9" />
            </div>
            <h2 className="text-2xl font-PxBold">Payment Done Successfully</h2>
            <p className="mt-3 font-PxThin text-white/80">
              Your subscription payment has been completed.
            </p>
            {paymentDetails.length > 0 && (
              <div className="mt-6 rounded-2xl border border-[#71777D] bg-white/5 p-4 text-left">
                {paymentDetails.map((item) => (
                  <div key={item.label} className="border-b border-white/10 py-2 last:border-b-0">
                    <p className="text-xs font-PoppinsL uppercase tracking-wide text-white/50">{item.label}</p>
                    <p className="mt-1 break-all text-sm font-PoppinsL text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={handlePaymentSuccessClose}
              className="mt-8 w-full rounded-full bg-gradient-to-t from-[#2590F3] via-[#26BBF8] to-[#2CF6FE] py-3 text-lg font-PoppinsL text-white"
            >
              Back To Home
            </button>
          </div>
        </Modal>
        <Modal isOpen={isPaymentFailedModalOpen} onClose={handlePaymentFailedClose}>
          <div className="px-8 py-10 text-center text-white">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20 border border-red-400">
              <img src={Times} alt="" className="h-9 w-9" />
            </div>
            <h2 className="text-2xl font-PxBold">Subscription Failed</h2>
            <p className="mt-3 font-PxThin text-white/80">
              Your subscription payment was not completed.
            </p>
            {paymentDetails.length > 0 && (
              <div className="mt-6 rounded-2xl border border-[#71777D] bg-white/5 p-4 text-left">
                {paymentDetails.map((item) => (
                  <div key={item.label} className="border-b border-white/10 py-2 last:border-b-0">
                    <p className="text-xs font-PoppinsL uppercase tracking-wide text-white/50">{item.label}</p>
                    <p className="mt-1 break-all text-sm font-PoppinsL text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={handlePaymentFailedClose}
              className="mt-8 w-full rounded-full bg-gradient-to-t from-[#2590F3] via-[#26BBF8] to-[#2CF6FE] py-3 text-lg font-PoppinsL text-white"
            >
              Back To Home
            </button>
          </div>
        </Modal>
    </>
  )
}

export default Home